import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import {
  ConfirmationDialogComponent,
  LoadingOverlayComponent,
  SearchableSingleSelectorItem,
  SnackbarService,
} from '@shared/ui';
import { Subject } from 'rxjs';
import { filter, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import type { MethodTypeAcquisitionField } from '../../../state/method-type.state';
import { EDITOR_COMMIT_MODE } from '../../editor-commit-mode.token';
import { MethodPipelineManagerComponent } from '../../pipelines/method-pipeline-manager/method-pipeline-manager.component';
import { ResourceManagerComponent } from '../../resources/resource-manager/resource-manager.component';
import { MethodAssetFieldsManagerComponent } from '../method-asset-fields-manager/method-asset-fields-manager.component';

import { MethodTypeAcquisitionFormComponent } from './method-type-acquisition-form.component';
import { MethodTypeEditorFacade } from './method-type-editor.facade';

/**
 * Smart method-type editor: the name field, the acquisition section, and the
 * three managed sections (resources, asset fields, pipelines). A pure draft
 * producer — save/cancel emit the shared state's one-shot outcome; persistence
 * belongs to the consumer (the host page runs the protocol cascade on saved).
 *
 * Deliberately declares NO providers: the host page owns the full DI scope
 * (editor trio, manager stacks, shared staging ledgers, cascade tier), which
 * also keeps the scope overridable by stories and specs (pipeline-editor
 * precedent).
 */
@Component({
  selector: 'ts-method-type-editor',
  templateUrl: './method-type-editor.component.html',
  styleUrls: ['./method-type-editor.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatDialogModule,
    LoadingOverlayComponent,
    MethodTypeAcquisitionFormComponent,
    MethodAssetFieldsManagerComponent,
    ResourceManagerComponent,
    MethodPipelineManagerComponent,
  ],
})
export class MethodTypeEditorComponent implements OnInit, OnDestroy {
  public readonly loading$ = this.facade.loading$;
  public readonly saving$ = this.facade.saving$;
  public readonly error$ = this.facade.error$;
  public readonly nameError$ = this.facade.nameError$;
  public readonly assetTypeError$ = this.facade.assetTypeError$;
  public readonly sampleNames$ = this.facade.sampleNames$;
  public readonly acquisitionFields$ = this.facade.acquisitionFields$;
  public readonly currentLanguage$ = this.facade.currentLanguage$;
  public readonly assetTypeOptions$ = this.facade.assetTypeOptions$;
  public readonly assetTypesLoaded$ = this.facade.assetTypesLoaded$;
  public readonly assetTypeSearchTerm$ = this.facade.assetTypeSearchTerm$;
  public readonly selectedAssetTypeItem$ = this.facade.selectedAssetTypeItem$;
  public readonly manualConfigMode$ = this.facade.manualConfigMode$;
  public readonly methodTypeConfig$ = this.facade.customConfigValue$;
  public readonly canExitManualConfig$ = this.facade.canExitCustomConfig$;
  public readonly configError$ = this.facade.configError$;

  public readonly nameControl = new FormControl<string>('', { nonNullable: true });

  /**
   * Confirm-button label follows the host's commit semantics: staging hosts
   * (dialog wrappers) read "Continue", persisting hosts (pages) read "Save".
   */
  public readonly confirmLabelKey =
    inject(EDITOR_COMMIT_MODE) === 'stage' ? 'button.continue' : 'button.save';

  private readonly destroy$ = new Subject<void>();

  constructor(
    public readonly facade: MethodTypeEditorFacade,
    private readonly dialog: MatDialog,
    private readonly snackbar: SnackbarService
  ) {}

  ngOnInit(): void {
    this.facade.initialize();

    // state → form (seeded once the load resolves; in create mode loading
    // never flips to true so this fires immediately with the empty default)
    this.facade.loading$
      .pipe(
        filter((loading) => !loading),
        take(1),
        withLatestFrom(this.facade.name$),
        takeUntil(this.destroy$)
      )
      .subscribe(([, name]) => this.nameControl.setValue(name, { emitEvent: false }));

    // form → state
    this.nameControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => this.facade.setName(value));

    // Cascade snackbar dispatchers (pipeline-editor parity): long duration
    // because the message references a specific label and the user needs time
    // to read it before deciding to retry.
    this.facade.labelSaveFailed$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ name }) =>
        this.snackbar.show('pipeline_editor.snackbar.label_save_failed', { duration: 0, params: { name } })
      );
    this.facade.labelConfigSaveFailed$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ name }) =>
        this.snackbar.show('pipeline_editor.snackbar.label_config_save_failed', { duration: 0, params: { name } })
      );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onSave(): void {
    this.facade.save();
  }

  public onCancel(): void {
    this.facade.hasChanges$.pipe(take(1), takeUntil(this.destroy$)).subscribe((hasChanges) => {
      if (!hasChanges) {
        this.facade.cancel();
        return;
      }
      this.dialog
        .open(ConfirmationDialogComponent, {
          data: {
            title: 'method_type_editor.warnings.cancel_unsaved.title',
            text: 'method_type_editor.warnings.cancel_unsaved.text',
            acceptButtonText: 'button.discard_changes',
            cancelButtonText: 'pipeline_step_editor.button.keep_editing',
            warningDialog: true,
            defaultAction: 'cancel',
          },
          hasBackdrop: true,
        })
        .afterClosed()
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe((result) => {
          if (result === 'accept') this.facade.cancel();
        });
    });
  }

  public onAssetTypeSelected(item: SearchableSingleSelectorItem): void {
    this.facade.selectAssetType(item);
  }

  public onAssetTypeSearchChanged(term: string): void {
    this.facade.searchAssetTypes(term);
  }

  public onAssetTypeLastItemReached(): void {
    // CONTINUOUS loading appends the next page from the internal token.
    this.facade.loadMoreAssetTypes(undefined);
  }

  public onSampleNameAdded(values: Record<string, string>): void {
    this.facade.addSampleName(values);
  }

  public onSampleNameRemoved(index: number): void {
    this.facade.removeSampleName(index);
  }

  public onSampleNameUpdated(change: { index: number; values: Record<string, string> }): void {
    this.facade.updateSampleName(change);
  }

  /**
   * Enabling manual config mode is destructive (a mistaken or missing key can
   * break the method type), so the component runs a warning dialog first and
   * only activates the JSON editor on explicit accept — the dumb form merely
   * signalled the intent.
   */
  public onManualConfigRequested(): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'method_type_editor.manual_config.warning.title',
          text: 'method_type_editor.manual_config.warning.text',
          acceptButtonText: 'method_type_editor.manual_config.warning.accept',
          cancelButtonText: 'button.cancel',
          warningDialog: true,
          defaultAction: 'cancel',
        },
        hasBackdrop: true,
      })
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result === 'accept') this.facade.enterCustomConfig();
      });
  }

  /**
   * Exiting manual mode is lossless (the button only enables when the JSON is
   * conventional and the chips hydrate from it), so unlike entering — which
   * warns first — no confirmation dialog runs.
   */
  public onManualConfigExitRequested(): void {
    this.facade.exitCustomConfig();
  }

  public onConfigChanged(event: { value: unknown | null; valid: boolean }): void {
    this.facade.setCustomConfig({
      value: event.valid ? (event.value as Record<string, unknown>) : null,
      valid: event.valid,
    });
  }

  public onFieldAdded(field: MethodTypeAcquisitionField): void {
    this.facade.addAcquisitionField(field);
  }

  public onFieldDeleted(index: number): void {
    this.facade.removeAcquisitionField(index);
  }
}
